import { CircularProgress, Divider, makeStyles, Typography } from '@material-ui/core'
import { Pagination } from '@material-ui/lab'
import React, { useEffect, useMemo, useState } from 'react'
import { useIsSmall } from './MediaHelpers'
import { v4 as uuid } from 'uuid'

export type TableColumn<T> = {
    title?: string,
    property: keyof T,
    align?: 'right' | 'left'
    flex?: number,
    render?: (value: unknown, row: T) => React.ReactNode,
    filler?: boolean
}

type TableProps<T extends object> = {
    columns: TableColumn<T>[],
    data: T[],
    collapse?: boolean,
    striped?: boolean,
    loading?: boolean,
    noRowMessage?: string,
    disableSmall?: boolean
}

const useStyles = makeStyles(theme => ({
    odd: {
        background: theme.palette.grey[200]
    },
    even: {
        background: theme.palette.background.default
    }
}))

const SmallTableDisplayRow = <T extends object>(props: TableProps<T> & { index: number, row: T }) => {
    const [open, setOpen] = useState(!props.collapse)
    const classes = useStyles()

    return (
        <div key={props.index} className={props.index % 2 === 0 ? classes.odd : classes.even} style={{ padding: 15 }}>
            {
                props.columns.filter(c => !c.filler).map((c, j) => {
                    const display = (
                        <div key={j} onClick={() => j === 0 ? setOpen(!open) : undefined} style={{ display: 'flex', flexDirection: 'column', marginTop: j === 0 ? 0 : 10 }}>
                            {c.title && <div style={{ fontWeight: 'bold', marginBottom: 10 }}>{c.title}</div>}
                            <div>{c.render ? c.render(props.row[c.property], props.row) : props.row[c.property]}</div>
                        </div>
                    )

                    return props.collapse
                        ? open || j === 0
                            ? display
                            : null
                        : display
                })
            }
        </div>
    )
}

export const EmptyContainer: React.FC = ({ children }) => <div style={{ margin: '60px 0', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>{children}</div>

const SmallTableDisplay = <T extends object>(props: TableProps<T>) =>
    <>
        {!props.loading && !props.data.length && <EmptyContainer><Typography variant='h5'>{props.noRowMessage}</Typography></EmptyContainer>}
        {props.loading && <EmptyContainer><CircularProgress size='4em' /></EmptyContainer>}
        {props.data.map((d, i) => <SmallTableDisplayRow key={i} {...props} row={d} index={i} />)}
    </>

const padding = '10px 5px'

const Table = <T extends object>(props: TableProps<T>) => {
    const isSmall = useIsSmall()
    const classes = useStyles()

    if (isSmall && !props.disableSmall) return <SmallTableDisplay {...props} />

    return (
        <div style={{ display: 'flex', flexWrap: 'nowrap', flexDirection: 'column' }}>
            <div style={{ display: 'flex', alignItems: 'flex-end' }}>
                {props.columns.map((c, i) => <div key={i} style={{ fontWeight: 'bold', flex: c.flex || 1, textAlign: c.align, overflow: 'hidden', padding }}>{c.title}</div>)}
            </div>
            <Divider />
            {!props.loading && !props.data.length && <EmptyContainer><Typography variant='h5'>{props.noRowMessage}</Typography></EmptyContainer>}
            {props.loading && <div style={{ margin: '60px 0', display: 'flex', alignItems: 'center', justifyContent: 'center' }}><CircularProgress size='4em' /></div>}
            {props.data.map((d, i) =>
                <div key={i} className={props.striped ? i % 2 === 0 ? classes.odd : classes.even : undefined} style={{ display: 'flex', alignItems: 'center' }}>
                    {props.columns.map((c, j) =>
                        <div key={j} style={{ flex: c.flex || 1, textAlign: c.align, overflow: 'hidden', padding }}>
                            {c.render ? c.render(d[c.property], d) : d[c.property]}
                        </div>
                    )}
                </div>
            )}
            <Divider />
        </div>
    )
}

type PaginatedTableProps<T extends object> = TableProps<T> & { page: number, setPage: React.Dispatch<number>, pageSize?: number }

export const PaginatedTable = <T extends object>({ page, pageSize = 10, setPage, data, ...props }: PaginatedTableProps<T>) => {
    const isSmall = useIsSmall()
    const [rows, setRows] = useState<T[]>([])
    const scrollPoint = useMemo(() => uuid(), [])

    useEffect(() => {
        setRows(data.slice(page * pageSize, page * pageSize + pageSize))
    }, [page, pageSize, data])

    const handleOnPage = (_: React.ChangeEvent<unknown>, p: number) => {
        document.getElementById(scrollPoint)?.scrollIntoView({ behavior: 'smooth' })
        setPage(p - 1)
    }

    return (
        <div id={scrollPoint}>
            <Table {...props} data={rows} />

            <div style={{ width: '100%', marginTop: 15 }}>
                <div style={{ float: 'right' }}>
                    <Pagination size={isSmall ? 'small' : 'medium'} color='primary' shape='rounded' count={Math.ceil(data.length / pageSize)} page={page + 1} onChange={handleOnPage} />
                </div>
            </div>
        </div>
    )
}

export default Table
